import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useParams } from 'react-router-dom'

// material-ui
import { styled, useTheme } from '@mui/material/styles'
import { AppBar, Box, CssBaseline, Toolbar } from '@mui/material'
import { IconChevronRight } from '@tabler/icons-react'

// project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs'
import MarketDetectionModal from 'modals/MarketDetectionModal'
import Header from './Header'
import Sidebar from './Sidebar'
import navigation from 'menu-items'
import { drawerWidth, smallDrawerWidth } from 'store/constant'
import { SET_MENU } from 'store/actions'
import { loadConfiguration } from 'redux/modules/configuration'

import useMarkets from 'hooks/useMarkets'
import useReduxAction from 'hooks/useReduxAction'

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  ...theme.typography.mainContent,
  ...(!open && {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.up('md')]: {
      marginLeft: 0,
      width: `calc(100% - ${smallDrawerWidth}px)`,
    },
    [theme.breakpoints.down('md')]: {
      marginLeft: '20px',
      width: `calc(100% - ${smallDrawerWidth}px)`,
      padding: '16px',
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: '10px',
      width: `calc(100% - ${smallDrawerWidth}px)`,
      padding: '16px',
      marginRight: '10px',
    },
  }),
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    width: `calc(100% - ${drawerWidth}px)`,
    [theme.breakpoints.down('md')]: {
      marginLeft: '20px',
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: '10px',
    },
  }),
}))

// ==============================|| MAIN LAYOUT ||============================== //
const MainLayout = () => {
  const theme = useTheme()

  const { currentUser, opened } = useSelector((state) => state.customization)
  const { offices } = useSelector((state) => state.entities)
  const { id: projectId } = useParams()

  const dispatch = useDispatch()

  const handleLeftDrawerToggle = () => {
    dispatch({ type: SET_MENU, opened: !opened })
  }

  useReduxAction('enums', 'loadEnums', 'MerchandiseGroup', [])
  useReduxAction('enums', 'loadEnums', 'PartType', [])
  useReduxAction('enums', 'loadEnums', 'PartSubType', [])
  useReduxAction('enums', 'loadEnums', 'ServiceType', [])
  useReduxAction('enums', 'loadEnums', 'UserRole', [])

  useReduxAction('configurations', 'loadConfiguration', {}, [], {
    dispatchAction: (action, requestOptions) => action('contentexpress', requestOptions),
  })

  // useReduxAction won't reload after a failure, so retry the configuration with backoff (2s, 4s, 8s, 16s, then every 30s).
  // Runs once per failure, so a retry only happens after the previous request has failed.
  const { failures: configurationFailures, loading: configurationLoading } = useSelector((state) => state.configurations)

  useEffect(() => {
    if (!configurationFailures || configurationLoading) return undefined

    const timeout = setTimeout(() => {
      dispatch(loadConfiguration('contentexpress'))
    }, Math.min(1000 * 2 ** configurationFailures, 30000))

    return () => clearTimeout(timeout)
  }, [configurationFailures])

  const { defaultOffice } = useMarkets()

  const officeId = currentUser?.office || defaultOffice

  useReduxAction('offices', 'loadOffice', {}, [officeId], {
    dispatchAction: (action, requestOptions) => action(officeId, requestOptions),
    shouldPerformFn: (entityReducer) => {
      const { errors, loading } = entityReducer
      return !loading && !errors.length && !!officeId && !offices[officeId]
    },
  })

  useReduxAction('projects', 'loadProject', {}, [projectId], {
    dispatchAction: (action, requestOptions) => action(projectId, requestOptions),
    shouldPerformFn: (entityReducer) => {
      const { errors, loaded, loading } = entityReducer
      return !loaded && !loading && !errors.length && projectId && !currentUser?.roles?.includes('Agent')
    },
  })

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      {/* header */}
      <AppBar
        enableColorOnDark
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.default,
          transition: opened ? theme.transitions.create('width') : 'none',
        }}
      >
        <Toolbar>
          <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
        </Toolbar>
      </AppBar>

      {/* drawer */}
      <Sidebar drawerOpen={opened} drawerToggle={handleLeftDrawerToggle} />

      {/* main content */}
      <Main open={opened}>
        {/* breadcrumb */}
        <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
        <Outlet />
      </Main>

      <MarketDetectionModal />
    </Box>
  )
}

export default MainLayout
